import { ContestWinnerStatusEnum, type SkillTestData } from '../../helpers/utils/types';

export const SKILL_TEST_MAX_ATTEMPTS = 3;
export const SKILL_TEST_ANSWER_DEADLINE_MINUTES = 10;
export const SKILL_TEST_EXPIRY_BUSINESS_DAYS = 21;

export type SkillTestTerminalVariant =
  | 'success'
  | 'failed'
  | 'expired'
  | 'already_completed'
  | 'invalid_token'
  | 'contest_closed';

export function computeRemainingSecondsFromDeadline(answerDeadline: string): number {
  return Math.max(0, Math.ceil((new Date(answerDeadline).getTime() - Date.now()) / 1000));
}

export function formatRemainingTime(totalSeconds: number): string {
  const seconds = Math.max(totalSeconds, 0);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export function getAttemptsRemaining(attemptCount: number): number {
  return Math.max(SKILL_TEST_MAX_ATTEMPTS - attemptCount, 0);
}

export function isDeadlinePassed(data: Pick<SkillTestData, 'answerDeadline'>): boolean {
  if (!data.answerDeadline) {
    return false;
  }

  return computeRemainingSecondsFromDeadline(data.answerDeadline) <= 0;
}

export function isSkillTestActive(data: SkillTestData): boolean {
  return (
    data.status === ContestWinnerStatusEnum.Pending &&
    Boolean(data.openedAt) &&
    Boolean(data.answerDeadline) &&
    !isDeadlinePassed(data) &&
    getAttemptsRemaining(data.attemptCount) > 0
  );
}

export function resolveInitialTerminalVariant(data: SkillTestData): SkillTestTerminalVariant | null {
  if (data.status === ContestWinnerStatusEnum.Awarded) {
    return 'success';
  }

  if (data.status === ContestWinnerStatusEnum.Expired) {
    return 'expired';
  }

  if (data.status === ContestWinnerStatusEnum.Forfeited) {
    return 'failed';
  }

  if (data.status === ContestWinnerStatusEnum.Cancelled) {
    return 'contest_closed';
  }

  if (data.status === ContestWinnerStatusEnum.Pending) {
    if (getAttemptsRemaining(data.attemptCount) <= 0) {
      return 'failed';
    }

    if (data.openedAt && data.answerDeadline && isDeadlinePassed(data)) {
      return 'expired';
    }

    return null;
  }

  return null;
}

export function mapForbiddenToTerminal(message: string): SkillTestTerminalVariant {
  if (message.includes(ContestWinnerStatusEnum.Expired)) {
    return 'expired';
  }

  if (message.includes(ContestWinnerStatusEnum.Forfeited)) {
    return 'failed';
  }

  if (message.includes(ContestWinnerStatusEnum.Awarded)) {
    return 'success';
  }

  if (message.includes(ContestWinnerStatusEnum.Cancelled)) {
    return 'contest_closed';
  }

  return 'contest_closed';
}

export function isDeadlinePassedError(message: string): boolean {
  return message === 'skill_test_deadline_passed';
}

export function isSkillTestNotOpenedError(message: string): boolean {
  return message === 'skill_test_not_opened';
}
